import React, { useEffect, useState } from "react";
import {
  BG_STYLES,
  BottomDrawer,
  Button,
  BUTTON_STATUS,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  CrossIcon,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from "@dehaat/dds";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import SearchInput from "app/components/reusable/SearchInput";
import RoundImageWithIcon from "app/components/reusable/RoundImageWithIcon";
import RoundImageWithName from "../reusable/RoundImageWithName";
import { DIMENSION_HEIGHT } from "app/utils/common";
import { Crop, CropsWithCategory } from "app/farmer360/KhetEdge/models/crop";
import fetchAllCrops from "app/farmer360/KhetEdge/apis/fetchAllCrops";
import { Dimensions } from "react-native";
import { useTranslation } from "react-i18next";

interface CropSelectionWithFlagProps {
  userCrop?: Crop;
  onClose: () => void;
  onSubmit: (crops: Crop) => void;
}

const AddCropModal = ({
  userCrop,
  onClose,
  onSubmit,
}: CropSelectionWithFlagProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [allCrops, setAllCrops] = useState<CropsWithCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [cropsToBeSearched, setCropsToBeSearched] = useState<Crop[]>([]);
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height
  );

  const [selectedCrops, setSelectedCrops] = useState<Crop | null>(
    userCrop ? userCrop : null
  );

  useEffect(() => {
    setScreenHeight(Dimensions.get("window").height);
  }, []);

  const searchResults = cropsToBeSearched.filter((crop) =>
    crop.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const fetchAndUpdateAllCrops = async () => {
    const response = await fetchAllCrops();
    if (response) {
      setAllCrops(response);
      const onlyCrops: Crop[] = [];
      response.forEach((data) => onlyCrops.push(...data.crops));
      setCropsToBeSearched(onlyCrops);
    }
  };

  const handleCropClick = (crop: Crop) => {
    setSelectedCrops(crop);
  };

  const handleSubmitCrops = async () => {
    if (selectedCrops) onSubmit(selectedCrops);
  };

  useEffect(() => {
    fetchAndUpdateAllCrops().finally(() => setLoading(false));
  }, []);
  return (
    <BottomDrawer
      visible
      modalStyle={{ height: screenHeight - 100 }}
      heading={t("select_growing_crop")}
      onClose={onClose}
      closeOnBackdropPress
    >
      <View style={[COMMON_STYLES.flex1]}>
        {loading && <Spinner type="on-screen" />}
        <SearchInput
          placeholder={t("search_crop")}
          onChange={(text: string) => setSearchQuery(text)}
          value={searchQuery}
        />

        {!searchQuery ? (
          <>
            <ScrollView style={[COMMON_STYLES["mt5/4"], COMMON_STYLES.flex1]}>
              {selectedCrops && (
                <View>
                  <Text
                    style={[
                      TYPOGRAPHY.TEXT1,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral80,
                    ]}
                  >
                    {t("selected_crop")}
                  </Text>
                  <View
                    style={[
                      COMMON_STYLES.row,
                      COMMON_STYLES["p0.5"],
                      COMMON_STYLES.justifyStart,
                      BG_STYLES.bgNeutral10,
                      COMMON_STYLES["mv3/4"],
                      COMMON_STYLES.br1,
                      COMMON_STYLES.flexWrap,
                    ]}
                  >
                    <RoundImageWithIcon
                      crop={selectedCrops}
                      key={selectedCrops.id}
                      containerStyle={[styles.margin, COMMON_STYLES.mv1]}
                      activeImagecontainerStyle={styles.selectedImageContainer}
                      isIcon
                      imageStyle={styles.image}
                      iconContainerStyle={styles.crossStyle}
                      icon={
                        <CrossIcon
                          height={16}
                          width={16}
                          strokeColor={"white"}
                        />
                      }
                      onClick={() => {}}
                      onIconClick={() => setSelectedCrops(null)}
                      isImageSelected
                    />
                  </View>
                </View>
              )}
              {allCrops.map((cat, index) => (
                <View key={index}>
                  <Text
                    style={[
                      TYPOGRAPHY.TEXT1,
                      VARIANTS_STYLES.MEDIUM,
                      TEXT_STYLES.textNeutral80,
                    ]}
                  >
                    {cat.category}
                  </Text>
                  <View
                    style={[
                      COMMON_STYLES.row,
                      COMMON_STYLES["p0.5"],
                      COMMON_STYLES.justifyStart,
                      BG_STYLES.bgNeutral10,
                      COMMON_STYLES["mv3/4"],
                      COMMON_STYLES.br1,
                      COMMON_STYLES.flexWrap,
                    ]}
                  >
                    {cat.crops.map((crop) => (
                      <RoundImageWithIcon
                        crop={crop}
                        key={crop.id}
                        containerStyle={[styles.margin, COMMON_STYLES.mv1]}
                        activeImagecontainerStyle={
                          styles.selectedImageContainer
                        }
                        isIcon
                        imageStyle={styles.image}
                        iconContainerStyle={styles.crossStyle}
                        icon={
                          <CrossIcon
                            height={16}
                            width={16}
                            strokeColor={"white"}
                          />
                        }
                        onClick={() => handleCropClick(crop)}
                        onIconClick={() => setSelectedCrops(null)}
                        isImageSelected={selectedCrops?.id === crop.id}
                      />
                    ))}
                  </View>
                </View>
              ))}
            </ScrollView>
            <View
              style={[COMMON_STYLES.row, COMMON_STYLES["pv1/2"], styles.gap1]}
            >
              <Button
                onClick={onClose}
                text={t("cancel")}
                type={BUTTON_TYPE.SECONDARY}
                size={SIZE.large}
                buttonStyle={COMMON_STYLES.flex1}
              />
              <Button
                onClick={handleSubmitCrops}
                text="save"
                size={SIZE.large}
                buttonStyle={COMMON_STYLES.flex1}
                status={BUTTON_STATUS.ACTIVE}
              />
            </View>
          </>
        ) : (
          <>
            {searchResults.length ? (
              <>
                <Text
                  style={[
                    COMMON_STYLES.mv1,
                    TYPOGRAPHY.HEADING3,
                    VARIANTS_STYLES.MEDIUM,
                    TEXT_STYLES.textNeutral70,
                  ]}
                >
                  {t("search_results")}
                </Text>
                <View style={[COMMON_STYLES.row, COMMON_STYLES.flexWrap]}>
                  {searchResults.map((crop) => (
                    <RoundImageWithName
                      key={crop.id}
                      name={crop.name}
                      imgUrl={crop.image_url}
                      handleItemClick={() => {
                        handleCropClick(crop);
                        setSearchQuery("");
                      }}
                      style={COMMON_STYLES["mv3/4"]}
                    />
                  ))}
                </View>
              </>
            ) : (
              <Text
                style={[
                  COMMON_STYLES.mv1,
                  TYPOGRAPHY.HEADING3,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral70,
                ]}
              >
                {t("no_crops_found")}
              </Text>
            )}
          </>
        )}
      </View>
    </BottomDrawer>
  );
};

const styles = StyleSheet.create({
  divider: {
    borderRightWidth: 1,
    borderColor: COLORS.neutral20,
    height: DIMENSION_HEIGHT,
  },
  dividerHz: {
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
  },
  margin: {
    marginHorizontal: 10,
    marginBottom: 4,
    width: 80,
  },

  image: {
    height: 80,
    width: 80,
  },

  selectedImageContainer: {
    borderColor: COLORS.primary100,
    borderWidth: 2,
  },

  crossStyle: {
    position: "absolute",
    top: 0,
    right: -3,
    zIndex: 1,
    padding: 5,
    borderRadius: 9999,
    backgroundColor: COLORS.primary100,
    borderWidth: 2,
    borderColor: COLORS.white,
  },

  gap1: {
    gap: 16,
  },
});

export default AddCropModal;
